/**
 * views/reporting/sections/TopOpenBids.jsx - the top open bids table.
 * Rendered by calling it from ReportingView.jsx with the values it reads,
 * so it renders the same DOM the view used to build inline.
 */
import { Icon } from '../../../components/ui/Icon';
import { Pill } from '../../../components/ui/Pill';
import { fmt } from '../../../utils/format';
import { exportCsv } from '../../../utils/exportCsv';
import { cleanLabel } from '../reportingHelpers';
import { CARD } from '../reportingTokens';
import { SectionHeader } from '../SectionHeader';

export function renderTopOpenBids({ topBidsSets, yearLabel, selectedYear, topBids, topBidsTotal }) {
  return (
    <>
      {/* TOP OPEN BIDS TABLE */}
      <SectionHeader
        title="Top Open Bids by Value"
        subtitle={`${topBidsSets.activeBids.length} active bids · ${yearLabel} — showing top 15`}
        explain={{
          title: 'Top Open Bids',
          body: 'The 15 highest-value Open bids in the selected year, sorted by quoted price. These are the live opportunities that move the Total Pipeline number the most.',
          formula: "Filter status = 'Open' · sort by quoted_price DESC · top 15",
        }}
        right={
          <button
            onClick={() =>
              exportCsv(
                `globalops_pipeline_${selectedYear === 'all' ? 'alltime' : Array.isArray(selectedYear) ? selectedYear.join('-') : selectedYear}.csv`,
                topBidsSets.activeBids.map((p) => ({
                  'Project Name': p.name || p.description || 'Unnamed Project',
                  Customer: p.customer,
                  'Sub Customer': p.subCustomer || '',
                  'Quote Date': p.quoteDate || '',
                  'Quoted Price': parseFloat(parseFloat(p.quotedPrice || 0).toFixed(2)),
                  Status: p.status,
                  Engineer: p.engineer || '',
                  Division: cleanLabel(p.division) || '',
                  Type: cleanLabel(p.type) || '',
                }))
              )
            }
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              padding: '5px 12px',
              borderRadius: 6,
              border: '1px solid #E8E8E8',
              background: '#fff',
              fontSize: 10,
              fontWeight: 600,
              color: '#666',
              cursor: 'pointer',
              fontFamily: 'Montserrat',
            }}
          >
            <Icon name="download" size={11} color="#666" /> Export Pipeline CSV
          </button>
        }
      />
      <div style={{ ...CARD, padding: 0, marginBottom: 16 }}>
        <div className="table-responsive" style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {['#', 'Project Name', 'Customer', 'Sub Customer', 'Quote Date', 'Quoted Price', 'Status'].map((h) => (
                  <th
                    key={h}
                    style={{
                      padding: '10px 16px',
                      textAlign: 'left',
                      fontSize: 9.5,
                      fontWeight: 700,
                      color: '#5A5A5A',
                      letterSpacing: 1.2,
                      textTransform: 'uppercase',
                      background: '#FAFBFC',
                      borderBottom: '1px solid #F0F0F0',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {topBids.map((p, i) => (
                <tr key={p.id || i} style={{ borderBottom: '1px solid #F7F7F7' }}>
                  <td style={{ padding: '10px 16px', fontSize: 10, color: '#8A8A8A', fontWeight: 700 }}>{i + 1}</td>
                  <td style={{ padding: '10px 16px', fontSize: 11, fontWeight: 700, color: '#1B5E20', maxWidth: 280 }}>
                    {p.name || p.description || 'Unnamed Project'}
                  </td>
                  <td style={{ padding: '10px 16px', fontSize: 11, color: '#555' }}>{p.customer}</td>
                  <td style={{ padding: '10px 16px', fontSize: 10, color: '#888' }}>{p.subCustomer || '-'}</td>
                  <td style={{ padding: '10px 16px', fontSize: 10, color: '#888' }}>{p.quoteDate || '-'}</td>
                  <td style={{ padding: '10px 16px', fontSize: 11, fontWeight: 700, color: '#0D0D0D' }}>{fmt(p.quotedPrice)}</td>
                  <td style={{ padding: '10px 16px' }}>
                    <Pill label={p.status} />
                  </td>
                </tr>
              ))}
              {topBids.length > 0 && (
                <tr style={{ background: '#FAFBFC', borderTop: '2px solid #E8E8E8' }}>
                  <td colSpan={5} style={{ padding: '10px 16px', fontSize: 10, fontWeight: 700, color: '#5A5A5A', textAlign: 'right' }}>
                    Total (top 15)
                  </td>
                  <td style={{ padding: '10px 16px', fontSize: 11, fontWeight: 800, color: '#1B5E20' }}>{fmt(topBidsTotal)}</td>
                  <td />
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
